import type { FlowMetadata } from '../types/flow';

/**
 * Shared helpers for building the Workflow Selector status chip label.
 *
 * Both the table view (`FlowTable`) and the grid view (`FlowGridView`) render
 * the same status chip text:
 *   - Completed flows  → "completed in N days" (or just "completed")
 *   - In-progress flows → "in progress (S/T) since N days ago"
 *
 * Extracted here so the two views can't drift. See `FlowTable.tsx` for the
 * original logic this was lifted from.
 */

function safeDate(iso?: string | null): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Whole-day count between the flow's creation and its completion (or
 * `updatedAt` fallback for legacy completed flows). Returns `null` when the
 * required timestamps are missing or the duration is negative.
 */
export function completedDaysCount(flow: FlowMetadata): number | null {
  const start = safeDate(flow.createdAt);
  const end = safeDate(flow.completedAt) ?? safeDate(flow.updatedAt);
  if (!start || !end) return null;
  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 0) return null;
  return Math.round(diffMs / 86400000);
}

/**
 * Whole-day count between the flow's creation timestamp and "now". Used to
 * render the "since N days ago" suffix on in-progress status chips. Returns
 * `null` when `createdAt` is missing.
 */
export function daysSinceCreated(flow: FlowMetadata): number | null {
  const start = safeDate(flow.createdAt);
  if (!start) return null;
  const diffMs = Date.now() - start.getTime();
  if (diffMs < 0) return null;
  return Math.round(diffMs / 86400000);
}

/**
 * Build the full status chip label given the derived status and the
 * pre-computed "S/T" stage fraction string (e.g. "8/12"). Both views call
 * this so they always produce identical chip text.
 *
 * - Completed: "completed in N days" or "completed" when N can't be computed.
 *   The (S/T) fraction is intentionally dropped for completed flows because
 *   it's always (total/total) and redundant.
 * - In progress: "in progress (S/T) since N days ago" — with the fraction and
 *   "since" suffix omitted when the underlying data is missing.
 */
export function buildStatusChipLabel(
  flow: FlowMetadata,
  status: 'completed' | 'in progress',
  lastActiveText: string
): string {
  if (status === 'completed') {
    const days = completedDaysCount(flow);
    return days == null ? 'completed' : `completed in ${days} days`;
  }
  const fraction =
    lastActiveText && lastActiveText !== '—' ? ` (${lastActiveText})` : '';
  const days = daysSinceCreated(flow);
  const suffix = days == null ? '' : ` since ${days} days ago`;
  return `${status}${fraction}${suffix}`;
}
